import { Link, useRouterState } from "@tanstack/react-router";
import { isSimplyAiEligible } from "@/lib/page-eligibility";
import { Suspense, lazy, useEffect, useState } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { NewsletterSection } from "./NewsletterSection";
import { MobileCTABar } from "./MobileCTABar";
import { AutoBreadcrumbs } from "./AutoBreadcrumbs";
import { CreditSection } from "./CreditSection";
import { LanguageSwitcher } from "./LanguageSwitcher";

import type { ReactNode } from "react";
import { usePageDates } from "./PageDatesBadge";
import { BADGE_LABELS, formatBadgeDate } from "@/lib/seo/page-freshness";

// Exit-intent is never needed for first paint — keep it out of the initial
// mobile bundle and only load it once the page has settled.
const ExitIntentModal = lazy(() =>
  import("./ExitIntentModal").then((m) => ({ default: m.ExitIntentModal })),
);

/**
 * Load non-critical widgets only after the first user interaction, or well after
 * the page has finished loading. Idle callbacks fire during the initial render
 * window on phones and made the ~600 KB chat bundle compete with first paint.
 */
function useAfterInteraction(fallbackMs: number) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    let timer = 0;
    const start = () => {
      setReady(true);
      events.forEach((e) => window.removeEventListener(e, start));
      window.clearTimeout(timer);
    };
    events.forEach((e) => window.addEventListener(e, start, { once: true, passive: true }));
    const arm = () => { timer = window.setTimeout(start, fallbackMs); };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return () => {
      events.forEach((e) => window.removeEventListener(e, start));
      window.removeEventListener("load", arm);
      window.clearTimeout(timer);
    };
  }, [fallbackMs]);
  return ready;
}

function DeferredExitIntent() {
  const ready = useAfterInteraction(8000);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ExitIntentModal />
    </Suspense>
  );
}

const ChatWidget = lazy(() =>
  import("./ChatWidget").then((m) => ({ default: m.ChatWidget })),
);

function DeferredChat() {
  const ready = useAfterInteraction(6000);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
}


export function SiteShell({
  children,
  hideNewsletter = false,
  productLabel,
  hideBreadcrumbs = false,
  hideCreditSection = false,
}: {
  children: ReactNode;
  hideNewsletter?: boolean;
  productLabel?: string;
  /** Set when the page renders its own <Breadcrumbs /> with richer labels. */
  hideBreadcrumbs?: boolean;
  /** Set when the page already renders <CreditSection /> itself. */
  hideCreditSection?: boolean;
}) {
  // Simply AI runs on every eligible public page — including business-purpose
  // investor pages outside FL/CO — but never on auth, thank-you or admin views.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const chatEligible = isSimplyAiEligible(pathname);
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      {!hideBreadcrumbs && <AutoBreadcrumbs />}
      <main className="flex-1 pb-16 lg:pb-0">{children}</main>
      {!hideCreditSection && <CreditSection />}
      {!hideNewsletter && <NewsletterSection productLabel={productLabel} />}
      <Footer />
      <DeferredExitIntent />
      {chatEligible && <DeferredChat />}

      <MobileCTABar />
      <LanguageSwitcher className="fixed bottom-4 left-4 z-40 hidden border-brand-gold bg-background text-foreground shadow-lg lg:flex" />
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  primaryHref = "/apply",
  primaryLabel = "Start My Approval",
  secondaryHref = "/rates",
  secondaryLabel = "See Today's Rates",
  image,
  updatedDate,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
  image?: { src: string; alt: string };
  updatedDate?: string;
}) {
  // Dates come only from the central page_freshness record; `updatedDate` is ignored.
  const { reviewed } = usePageDates();
  const isEs = useRouterState({ select: (st) => /^\/es(\/|$)/.test(st.location.pathname) });
  return (
    <div className="page-hero relative overflow-hidden bg-brand-gradient text-primary-foreground">
      <div className="container mx-auto max-w-7xl px-4 py-16 md:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-[1.35fr_1fr]">
          <div className="max-w-3xl">
            {eyebrow && (
              <div className="mb-3 inline-flex items-center rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-gold">
                {eyebrow}
              </div>
            )}
            <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">{title}</h1>
            {subtitle && (
              <div className="mt-5 max-w-2xl text-lg text-primary-foreground/90">{subtitle}</div>
            )}
            {reviewed && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 bg-brand-gold/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-gold">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
                {BADGE_LABELS[isEs ? "es" : "en"].reviewed}{" "}
                <time dateTime={reviewed}>{formatBadgeDate(reviewed, isEs ? "es" : "en")}</time>
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={primaryHref}
                className="rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold text-primary shadow-sm transition-colors hover:bg-brand-gold-hover"
              >
                {primaryLabel}
              </Link>
              <Link
                to={secondaryHref}
                className="rounded-md border border-primary-foreground/40 bg-transparent px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                {secondaryLabel}
              </Link>
            </div>
          </div>
          {image && (
            <div className="relative hidden lg:block">
              <div className="relative overflow-hidden rounded-2xl border border-primary-foreground/20 shadow-2xl">
                <picture>
                  {/* The hero art is hidden below lg — only desktop viewports fetch it. */}
                  <source media="(min-width: 1024px)" srcSet={image.src} />
                  <img
                    src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600'%3E%3C/svg%3E"
                    alt={image.alt}
                    width={800}
                    height={600}
                    fetchPriority="high"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </picture>

                <div className="absolute inset-0 bg-gradient-to-t from-brand/60 via-transparent to-transparent" aria-hidden="true" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
